/**
 * Platform-free UI helpers (04-architecture § 7): variant maps, accessibility
 * labels and the floor-plan geometry both renderers share.
 */

import { deskState, type DeskState } from '../tokens/index.js';

export type { DeskState };

/** 03 § 4.12 — the exact fill, stroke and label colour per desk state. */
export function deskStyle(state: DeskState) {
  return deskState[state];
}

export interface PlanObject {
  resourceId: string;
  label: string;
  zone: string | null;
  xCm: number;
  yCm: number;
  wCm: number;
  hCm: number;
  rotationDeg: number;
}

export interface ViewBox {
  minX: number;
  minY: number;
  width: number;
  height: number;
}

/** cm → SVG viewBox, with a margin so strokes are not clipped. */
export function viewBoxFor(objects: readonly PlanObject[], marginCm = 50): ViewBox {
  if (objects.length === 0) return { minX: 0, minY: 0, width: 100, height: 100 };
  const xs = objects.flatMap((o) => [o.xCm, o.xCm + o.wCm]);
  const ys = objects.flatMap((o) => [o.yCm, o.yCm + o.hCm]);
  const minX = Math.min(...xs) - marginCm;
  const minY = Math.min(...ys) - marginCm;
  return {
    minX,
    minY,
    width: Math.max(...xs) - minX + marginCm,
    height: Math.max(...ys) - minY + marginCm,
  };
}

/** Hit rectangle in view coordinates, used by both the SVG and the RN renderer. */
export function hitRect(object: PlanObject): { x: number; y: number; width: number; height: number } {
  return { x: object.xCm, y: object.yCm, width: object.wCm, height: object.hCm };
}

/** BR-CHK-03 — nearest free desks, same zone first, then by plan distance. */
export function nearestDesks<T extends PlanObject>(
  from: PlanObject,
  candidates: readonly T[],
  limit = 3,
): T[] {
  return [...candidates]
    .filter((candidate) => candidate.resourceId !== from.resourceId)
    .sort((a, b) => {
      const zoneA = a.zone === from.zone ? 0 : 1;
      const zoneB = b.zone === from.zone ? 0 : 1;
      return zoneA - zoneB || distance(from, a) - distance(from, b);
    })
    .slice(0, limit);
}

const distance = (a: PlanObject, b: PlanObject): number => Math.hypot(a.xCm - b.xCm, a.yCm - b.yCm);

/**
 * 03 § 9 — a desk in a plan is a button for a screen reader; the label has to
 * carry the state, because colour alone never does.
 */
export function deskAccessibilityLabel(
  object: PlanObject,
  state: DeskState,
  t: (key: string) => string,
): string {
  const zone = object.zone ? `, ${object.zone}` : '';
  const stateLabel = t(`plan.legend.${state === 'mine' ? 'mine' : state === 'free' ? 'free' : 'taken'}`);
  return `${object.label}${zone} — ${stateLabel}`;
}
